<!-- app-subdomain-creators\src\Components\CreatorChatVoiceMemoPlayer.vue -->

<template>
  <div
    class="flex items-center border border-zinc-400 rounded-lg w-full sm:w-[260px] xl:w-[360px] h-[60px] bg-zinc-800 relative px-2 justify-between group"
  >
    <!-- Hidden Audio Element -->
    <audio
      ref="audioEl"
      :src="src"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @ended="onEnded"
    ></audio>

    <div class="w-full h-full flex items-center gap-1">
      <!-- Play / Pause -->
      <div class="p-2">
        <div
          @click="togglePlay"
          class="w-10 h-10 cursor-pointer rounded-full flex items-center justify-center p-2 hover:bg-[#4ecdec] bg-[#32b9db] transition duration-200 ease-in-out transform"
        >
          <PlayIcon v-if="!isPlaying" class="text-zinc-200" />
          <PauseIcon v-else class="text-zinc-200" />
        </div>
      </div>

            <!-- Bars (clickable scrubber) -->
            <div
            ref="waveformEl"
            class="w-full relative my-1 flex justify-between items-center cursor-pointer"
            @click="onWaveformClick"
            >
            <div
                v-for="bar in bars"
                :key="bar.id"
                class="w-1 mx-0.5 rounded-sm"
                :class="bar.id <= filledBars ? 'bg-[#32b9db]' : 'bg-gray-500'"
                :style="{ height: bar.height + 'px' }"
            ></div>
            </div>


      <!-- Time -->
      <div class="flex items-center space-x-4">
        <span class="text-zinc-200 text-base font-semibold ml-2 min-w-[50px]">
          {{ formattedTime }}
        </span>
      </div>
    </div>

    <!-- Optional delete button (for input area) -->
    <div
      v-if="showDelete"
      class="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity"
      @mouseenter="showDeleteTooltip = true"
      @mouseleave="showDeleteTooltip = false"
    >
      <button
        class="bg-zinc-300 hover:bg-zinc-200 w-6 h-6 rounded-full transition duration-200 ease-in-out transform hover:scale-105"
        type="button"
        @click="$emit('delete')"
      >
        <XMarkIcon class="w-4 text-zinc-800 mx-auto" />
      </button>

      <div
        v-if="showDeleteTooltip"
        class="absolute text-center w-32 bottom-full p-2 -translate-x-14 mb-3 bg-black opacity-80 text-white text-sm rounded-lg tooltip z-10 ease-in-out"
      >
        delete audio clip
        <div
          class="tooltip-arrow absolute left-1/2 -right-4 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-black"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { PlayIcon, PauseIcon } from '@heroicons/vue/24/solid';
import { XMarkIcon } from '@heroicons/vue/24/outline';

defineProps({
  src: {
    type: String,
    required: true,
  },
  showDelete: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['delete']);

const audioEl = ref(null);
const waveformEl = ref(null); // 👈 container for the bars

const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);

const showDeleteTooltip = ref(false);

// random bar heights per instance
const bars = ref(
  Array.from({ length: 24 }).map((_, idx) => ({
    id: idx + 1,
    height: 8 + Math.round(Math.random() * 20),
  }))
);

const filledBars = ref(0);

const formattedTime = computed(() => {
  // If we've started playing (or scrubbed), show currentTime.
  // Otherwise, show total duration (once it's known).
  const baseSeconds =
    currentTime.value > 0 || isPlaying.value
      ? currentTime.value
      : (duration.value || 0);

  const totalSeconds = Math.floor(baseSeconds || 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

const syncBars = () => {
  const dur = duration.value || audioEl.value?.duration || 0;
  if (!dur || !bars.value.length) {
    filledBars.value = 0;
    return;
  }
  const progress = Math.min(currentTime.value / dur, 1);
  filledBars.value = Math.round(progress * bars.value.length);
};

const onTimeUpdate = () => {
  if (!audioEl.value) return;
  currentTime.value = audioEl.value.currentTime;
  syncBars();
};

const onLoadedMetadata = () => {
  if (!audioEl.value) return;
  duration.value = audioEl.value.duration || 0;
  // ensure bars & time reflect full duration initially
  syncBars();
};

const onEnded = () => {
  isPlaying.value = false;
  currentTime.value = duration.value || 0;
  syncBars();
};

const togglePlay = async () => {
  if (!audioEl.value) return;

  if (isPlaying.value) {
    audioEl.value.pause();
    isPlaying.value = false;
    return;
  }

  try {
    await audioEl.value.play();
    isPlaying.value = true;
  } catch (err) {
    console.warn('[CreatorChatVoiceMemoPlayer] play() failed', err);
  }
};

// 👇 NEW: click-to-seek on the waveform
const onWaveformClick = (event) => {
  if (!audioEl.value) return;

  const rect = waveformEl.value?.getBoundingClientRect();
  const dur = duration.value || audioEl.value.duration || 0;
  if (!rect || !dur) return;

  const clickX = event.clientX - rect.left;
  const ratio = Math.min(Math.max(clickX / rect.width, 0), 1);
  const newTime = ratio * dur;

  audioEl.value.currentTime = newTime;
  currentTime.value = newTime;
  syncBars();
};

onMounted(() => {
  syncBars();
});

onBeforeUnmount(() => {
  if (audioEl.value) {
    audioEl.value.pause();
  }
});
</script>


